import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import ReportButton from '../components/ReportButton';
import { UPLOADS_URL } from '../config';

const ProjectDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const highlightedTaskId = searchParams.get('task'); // ID de la tâche à mettre en avant
  const [project, setProject] = useState(null);
  const [images, setImages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isPaymentBlocked, setIsPaymentBlocked] = useState(false);
  const [blockingInfo, setBlockingInfo] = useState(null);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Ref pour scroller vers les postes
  const tasksRef = useRef(null);
  const highlightedTaskRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [projectRes, imagesRes, tasksRes, teamRes] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/projects/${id}/images`).catch(() => ({ data: [] })),
          api.get(`/tasks?project_id=${id}`).catch(() => ({ data: [] })),
          api.get(`/projects/${id}/team`).catch(() => ({ data: [] }))
        ]);
        
        setProject(projectRes.data);
        setImages(imagesRes.data || []);
        setTasks(tasksRes.data || []);
        setTeam(teamRes.data || []);
        
        // Vérifier si le projet est bloqué (pour auteur/collaborateurs)
        if (user && (projectRes.data.author_id === user.id || teamRes.data?.some(m => m.id === user.id))) {
          try {
            const accessRes = await api.get(`/project-finances/${id}/access`);
            if (accessRes.data.blocked && accessRes.data.reason === 'payment_blocked') {
              setIsPaymentBlocked(true);
              setBlockingInfo(accessRes.data.blocking_info);
            }
          } catch (err) {
            // Ignorer les erreurs d'accès
          }
        }
        
        if (user) {
          try {
            const applicationsRes = await api.get(`/applications/project/${id}`);
            setMyApplications(applicationsRes.data || []);
          } catch (err) {
            setMyApplications([]);
          }
        }
        
        // Si on a un ID de tâche dans l'URL, aller directement à l'onglet tasks
        if (highlightedTaskId) {
          setActiveTab('tasks');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, user, highlightedTaskId]);

  // Scroller vers la tâche mise en avant après le chargement
  useEffect(() => {
    if (!loading && highlightedTaskId && activeTab === 'tasks' && highlightedTaskRef.current) {
      setTimeout(() => {
        highlightedTaskRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [loading, highlightedTaskId, activeTab]);

  const getMyApplicationForTask = (taskId) => {
    return myApplications.find(app => String(app.task_id) === String(taskId));
  };

  const getApplicationStatusBadge = (status) => {
    const styles = {
      en_attente: { bg: 'bg-yellow-100 text-yellow-700', label: '⏳ En attente' },
      accepte: { bg: 'bg-green-100 text-green-700', label: '✅ Acceptée' },
      refuse: { bg: 'bg-red-100 text-red-700', label: '❌ Refusée' }
    };
    return styles[status] || styles.en_attente;
  };

  const getRoleStatusBadge = (task) => {
    if (task.assigned_user_id) {
      return { bg: 'bg-purple-100 text-purple-700', label: '🔒 Pourvu' };
    }
    return { bg: 'bg-green-100 text-green-700', label: '🟢 Ouvert' };
  };

  const getStatusBadge = (status) => {
    const styles = {
      actif: { bg: 'bg-green-100 text-green-700', label: '🟢 Actif', icon: '🟢' },
      en_cours: { bg: 'bg-blue-100 text-blue-700', label: '🔵 En cours', icon: '🔵' },
      termine: { bg: 'bg-gray-100 text-gray-700', label: '✅ Terminé', icon: '✅' },
      annule: { bg: 'bg-red-100 text-red-700', label: '❌ Annulé', icon: '❌' }
    };
    return styles[status] || { bg: 'bg-gray-100 text-gray-700', label: status, icon: '📋' };
  };

  // Vérifier si l'utilisateur actuel est un collaborateur/associé du projet
  const isUserCollaborator = () => {
    if (!user || !team || team.length === 0) return false;
    // L'API retourne 'id' pas 'user_id'
    return team.some(member => String(member.id) === String(user.id));
  };

  // Fonction pour scroller vers les postes ouverts
  const scrollToTasks = () => {
    setActiveTab('tasks');
    setTimeout(() => {
      if (tasksRef.current) {
        tasksRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Calculer le nombre de postes ouverts disponibles pour postuler
  const openTasksCount = tasks.filter(t => !t.assigned_user_id).length;
  const canApply = user && user.id !== project?.author_id && !isUserCollaborator();

  const getCategoryInfo = (cat) => {
    const categories = {
      commerce: { icon: '🛒', label: 'Commerce / E-commerce', color: 'from-orange-500 to-amber-500' },
      tech: { icon: '💻', label: 'Tech / Startup', color: 'from-blue-500 to-cyan-500' },
      services: { icon: '🔧', label: 'Services', color: 'from-gray-500 to-slate-500' },
      agriculture: { icon: '🌾', label: 'Agriculture / Agrobusiness', color: 'from-green-500 to-emerald-500' },
      immobilier: { icon: '🏠', label: 'Immobilier / BTP', color: 'from-yellow-600 to-orange-500' },
      transport: { icon: '🚚', label: 'Transport / Logistique', color: 'from-indigo-500 to-blue-500' },
      restauration: { icon: '🍽️', label: 'Restauration / Food', color: 'from-red-500 to-pink-500' },
      mode: { icon: '👗', label: 'Mode / Textile', color: 'from-pink-500 to-rose-500' },
      sante: { icon: '💊', label: 'Santé / Bien-être', color: 'from-teal-500 to-cyan-500' },
      education: { icon: '📚', label: 'Éducation / Formation', color: 'from-purple-500 to-violet-500' },
      finance: { icon: '💰', label: 'Finance / Assurance', color: 'from-emerald-500 to-green-500' },
      autre: { icon: '📌', label: 'Autre', color: 'from-gray-500 to-gray-600' }
    };
    return categories[cat] || categories.autre;
  };

  const getTaskTypeInfo = (type) => {
    const types = {
      collaboration: { icon: '🤝', label: 'Associé / Co-fondateur', color: 'bg-purple-100 text-purple-700' },
      prestation: { icon: '💰', label: 'Prestataire / Freelance', color: 'bg-green-100 text-green-700' },
      emploi: { icon: '💼', label: 'Employé / CDD-CDI', color: 'bg-blue-100 text-blue-700' },
      stage: { icon: '🎓', label: 'Stagiaire', color: 'bg-orange-100 text-orange-700' },
      partenariat: { icon: '🤝', label: 'Partenaire commercial', color: 'bg-indigo-100 text-indigo-700' }
    };
    return types[type] || { icon: '📋', label: type || 'Autre', color: 'bg-gray-100 text-gray-700' };
  };

  const getNiveauInfo = (niveau) => {
    const niveaux = {
      tous: { icon: '🌟', label: 'Tous niveaux' },
      debutant: { icon: '🌱', label: 'Débutant' },
      intermediaire: { icon: '📈', label: 'Intermédiaire' },
      expert: { icon: '🏆', label: 'Expert' }
    };
    return niveaux[niveau] || niveaux.tous;
  };

  const formatBudget = (budget) => {
    if (!budget) return null;
    return Number(budget).toLocaleString('fr-FR') + ' XOF';
  };

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-indigo-600 font-medium">Chargement du projet...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-100">
        <div className="text-center bg-white rounded-2xl shadow-xl p-12">
          <span className="text-6xl mb-4 block">😕</span>
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Projet non trouvé</h2>
          <p className="text-gray-500 mb-6">Ce projet n'existe pas ou a été supprimé.</p>
          <Link to="/projects" className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition">
            Voir tous les projets
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusBadge(project.statut);
  const categoryInfo = getCategoryInfo(project.categorie);
  const niveauInfo = getNiveauInfo(project.niveau_requis);
  const tags = project.tags ? (typeof project.tags === 'string' ? JSON.parse(project.tags) : project.tags) : [];
  const coverImage = project.image_couverture ? `${UPLOADS_URL}projects/${project.image_couverture}` : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Bandeau d'alerte si projet bloqué */}
        {isPaymentBlocked && user && (user.id === project.author_id || isUserCollaborator()) && (
          <div className="mb-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl shadow-lg p-4 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🚫</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg">Projet temporairement bloqué</h3>
                  <p className="text-white/90 text-sm">
                    {user.id === project.author_id 
                      ? `Retard de paiement plateforme (${blockingInfo?.days_blocked || '?'} jours). Le workspace est inaccessible.`
                      : 'Ce projet est bloqué par le propriétaire pour un retard de paiement.'}
                  </p>
                </div>
              </div>
              {user.id === project.author_id && (
                <Link
                  to={`/projects/${project.id}/finances/payments`}
                  className="px-6 py-3 bg-white text-red-600 rounded-xl font-bold hover:bg-gray-100 transition whitespace-nowrap"
                >
                  💳 Régulariser maintenant
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="mb-6">
          <button 
            onClick={() => navigate(-1)} 
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium transition"
          >
            <span>←</span> Retour
          </button>
        </div>

        {/* Hero Section avec image de couverture */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8">
          {/* Image de couverture */}
          <div className={`h-64 md:h-80 relative ${!coverImage ? `bg-gradient-to-br ${categoryInfo.color}` : ''}`}>
            {coverImage ? (
              <img src={coverImage} alt={project.titre} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-9xl opacity-30">{categoryInfo.icon}</span>
              </div>
            )}
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            
            {/* Badges sur l'image */}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${statusInfo.bg} backdrop-blur-sm`}>
                {statusInfo.label}
              </span>
              {project.categorie && (
                <span className="px-4 py-2 rounded-full text-sm font-semibold bg-white/90 text-gray-800 backdrop-blur-sm">
                  {categoryInfo.icon} {categoryInfo.label}
                </span>
              )}
            </div>

            {/* Actions (auteur, admin ou collaborateur) */}
            {user && (user.id === project.author_id || user.role === 'admin' || isUserCollaborator()) && (
              <div className="absolute top-4 right-4 flex gap-2">
                <Link
                  to={`/projects/${project.id}/workspace`}
                  className="px-4 py-2 bg-purple-600 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-purple-700 transition flex items-center gap-2"
                >
                  💼 Gestionnaire du projet
                </Link>
                <Link
                  to={`/projects/${project.id}/finances`}
                  className="px-4 py-2 bg-orange-500 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-blue-300 transition flex items-center gap-2"
                >
                  📊 Paiement & collaborateurs
                </Link>
                {/* Bouton Modifier uniquement pour l'auteur ou admin */}
                {(user.id === project.author_id || user.role === 'admin') && (
                  <Link
                    to={`/edit-project/${project.id}`}
                    className="px-4 py-2 bg-white/90 backdrop-blur-sm text-indigo-700 rounded-xl font-semibold hover:bg-white transition"
                  >
                    ✏️ Modifier
                  </Link>
                )}
              </div>
            )}

            {/* Titre sur l'image */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg">
                {project.titre}
              </h1>
              {project.description_courte && (
                <p className="text-white/90 text-lg max-w-3xl drop-shadow">
                  {project.description_courte}
                </p>
              )}
              
              {/* Bouton Postuler bien visible */}
              {openTasksCount > 0 && canApply && !highlightedTaskId && (
                <button
                  onClick={scrollToTasks}
                  className="mt-4 inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-2xl font-bold text-lg shadow-2xl hover:shadow-green-500/50 hover:scale-105 transition-all duration-300 animate-pulse hover:animate-none"
                >
                  <span className="text-2xl">🚀</span>
                  Postuler maintenant
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                    {openTasksCount} poste{openTasksCount > 1 ? 's' : ''} ouvert{openTasksCount > 1 ? 's' : ''}
                  </span>
                </button>
              )}
              
              {/* Bouton Postuler pour tâche spécifique (depuis "Pour Toi") */}
              {highlightedTaskId && canApply && tasks.find(t => String(t.id) === String(highlightedTaskId) && !t.assigned_user_id) && (
                <Link
                  to={`/apply/${highlightedTaskId}`}
                  className="mt-4 inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-2xl font-bold text-lg shadow-2xl hover:shadow-green-500/50 hover:scale-105 transition-all duration-300 animate-pulse hover:animate-none"
                >
                  <span className="text-2xl">🚀</span>
                  Postuler à ce poste
                </Link>
              )}
              
              {/* Message pour non-connectés */}
              {openTasksCount > 0 && !user && (
                <Link
                  to="/login"
                  className="mt-4 inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-2xl font-bold text-lg shadow-2xl hover:shadow-indigo-500/50 hover:scale-105 transition-all duration-300"
                >
                  <span className="text-2xl">🔐</span>
                  Connectez-vous pour postuler
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                    {openTasksCount} poste{openTasksCount > 1 ? 's' : ''}
                  </span>
                </Link>
              )}
            </div>
          </div>

          {/* Suspension Alert */}
          {project.is_suspended && (
            <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚠️</span>
                <div>
                  <h3 className="font-semibold text-red-700">Ce projet a été suspendu</h3>
                  {project.suspension_reason && (
                    <p className="text-red-600 text-sm mt-1">{project.suspension_reason}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Infos rapides */}
          <div className="p-6 md:p-8 border-b">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {/* Auteur */}
              <Link
                to={`/profile/${project.author_id}`}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-indigo-50 transition group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                  {project.author_nom ? project.author_nom.charAt(0).toUpperCase() : '?'}
                </div>
                <div>
                  <p className="text-xs text-gray-500">Porteur</p>
                  <p className="font-semibold text-gray-800 group-hover:text-indigo-600 text-sm truncate">
                    {project.author_nom || 'Auteur'}
                  </p>
                </div>
              </Link>

              {/* Lieu */}
              {(project.lieu_ville || project.lieu_pays) && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-xl">
                    📍
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Localisation</p>
                    <p className="font-semibold text-gray-800 text-sm">
                      {project.lieu_ville}{project.lieu_ville && project.lieu_pays ? ', ' : ''}{project.lieu_pays}
                    </p>
                  </div>
                </div>
              )}

              {/* Budget */}
              {project.budget && (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-xl">
                    💰
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Budget</p>
                    <p className="font-semibold text-green-700 text-sm">{formatBudget(project.budget)}</p>
                  </div>
                </div>
              )}

              {/* Deadline */}
              {project.deadline && (
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-xl">
                    📅
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Échéance</p>
                    <p className="font-semibold text-orange-700 text-sm">{formatDate(project.deadline)}</p>
                  </div>
                </div>
              )}

              {/* Niveau */}
              {project.niveau_requis && (
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-xl">
                    {niveauInfo.icon}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Niveau</p>
                    <p className="font-semibold text-purple-700 text-sm">{niveauInfo.label}</p>
                  </div>
                </div>
              )}

              {/* Type de projet */}
              {project.type_projet && (
                <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-xl">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-xl">
                    {project.type_projet === 'ponctuel' ? '⚡' : '🔄'}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Type</p>
                    <p className="font-semibold text-indigo-700 text-sm capitalize">{project.type_projet}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Liens externes */}
            {(project.lien_site || project.lien_social) && (
              <div className="mt-4 flex flex-wrap gap-3">
                {project.lien_site && (
                  <a 
                    href={project.lien_site} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition text-sm font-medium"
                  >
                    🌐 Site web
                  </a>
                )}
                {project.lien_social && (
                  <a 
                    href={project.lien_social} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-pink-50 text-pink-700 rounded-xl hover:bg-pink-100 transition text-sm font-medium"
                  >
                    📱 Réseaux sociaux
                  </a>
                )}
              </div>
            )}

            {/* Report Button */}
            {user && user.id !== project.author_id && (
              <div className="mt-4">
                <ReportButton 
                  targetType="project" 
                  targetId={project.id} 
                  targetTitle={project.titre}
                />
              </div>
            )}
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <div className="flex border-b">
            {[
              { id: 'overview', label: 'Présentation', icon: '📋', count: null },
              { id: 'tasks', label: 'Postes ouverts', icon: '💼', count: tasks.length },
              { id: 'team', label: 'Équipe', icon: '👥', count: team.length },
              { id: 'gallery', label: 'Galerie', icon: '📸', count: images.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 px-4 text-center font-medium transition flex items-center justify-center gap-2 ${
                  activeTab === tab.id 
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.count !== null && tab.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid md:grid-cols-3 gap-6">
              {/* Description principale */}
              <div className="md:col-span-2 bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 p-2 rounded-lg">📝</span>
                  Description du projet
                </h2>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                    {project.description || 'Aucune description disponible.'}
                  </p>
                </div>
              </div>

              {/* Sidebar stats */}
              <div className="space-y-6">
                {/* Stats Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">📊 En chiffres</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-xl">
                      <span className="text-gray-600">Postes ouverts</span>
                      <span className="font-bold text-indigo-600 text-lg">{tasks.filter(t => !t.assigned_user_id).length}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                      <span className="text-gray-600">Postes pourvus</span>
                      <span className="font-bold text-green-600 text-lg">{tasks.filter(t => t.assigned_user_id).length}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                      <span className="text-gray-600">Membres</span>
                      <span className="font-bold text-purple-600 text-lg">{team.length}</span>
                    </div>
                  </div>
                </div>

                {/* Auteur Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">👤 Porteur du projet</h3>
                  <Link
                    to={`/profile/${project.author_id}`}
                    className="flex items-center gap-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl hover:from-indigo-100 hover:to-purple-100 transition-all group"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg group-hover:scale-105 transition-transform">
                      {project.author_nom ? project.author_nom.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 group-hover:text-indigo-600 transition-colors">
                        {project.author_nom || 'Auteur inconnu'}
                      </h4>
                      <p className="text-sm text-gray-500">Voir le profil →</p>
                    </div>
                  </Link>
                </div>

                {/* Date création */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">📅 Timeline</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="text-sm text-gray-500">Créé le</p>
                        <p className="font-medium text-gray-800">{formatDate(project.date_creation)}</p>
                      </div>
                    </div>
                    {project.date_demarrage ? (
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <div>
                          <p className="text-sm text-gray-500">Démarré le</p>
                          <p className="font-medium text-blue-600">{formatDate(project.date_demarrage)}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                        <div>
                          <p className="text-sm text-gray-500">Démarrage</p>
                          <p className="font-medium text-gray-400 italic">Non démarré</p>
                        </div>
                      </div>
                    )}
                    {project.deadline && (
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                        <div>
                          <p className="text-sm text-gray-500">Échéance</p>
                          <p className="font-medium text-orange-600">{formatDate(project.deadline)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tasks/Roles Tab */}
          {activeTab === 'tasks' && (
            <div ref={tasksRef} className="space-y-4">
              {tasks.length > 0 ? (
                tasks.map(task => {
                  const isAssignedToMe = task.assigned_user_id && user && String(task.assigned_user_id) === String(user.id);
                  const isAssignedToOther = task.assigned_user_id && (!user || String(task.assigned_user_id) !== String(user.id));
                  const myApplication = user ? getMyApplicationForTask(task.id) : null;
                  const hasAlreadyApplied = !!myApplication;
                  const roleStatus = getRoleStatusBadge(task);
                  const typeInfo = getTaskTypeInfo(task.type);
                  const isHighlighted = highlightedTaskId && String(task.id) === String(highlightedTaskId);
                  
                  return (
                    <div 
                      key={task.id}
                      ref={isHighlighted ? highlightedTaskRef : null}
                      className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-500 ${
                        isAssignedToMe ? 'ring-2 ring-green-500' : ''
                      } ${isHighlighted ? 'ring-4 ring-indigo-500 ring-offset-2 shadow-2xl scale-[1.02]' : ''}`}
                    >
                      <div className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                          {/* Icône et infos principales */}
                          <div className="flex-1">
                            <div className="flex items-start gap-4 mb-4">
                              <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${typeInfo.color}`}>
                                {typeInfo.icon}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <h3 className="text-xl font-bold text-gray-800">{task.titre}</h3>
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${roleStatus.bg}`}>
                                    {roleStatus.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                                    {typeInfo.label}
                                  </span>
                                  {isAssignedToMe && (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                      ✅ Assigné à vous
                                    </span>
                                  )}
                                  {hasAlreadyApplied && !isAssignedToMe && (
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getApplicationStatusBadge(myApplication.statut).bg}`}>
                                      {getApplicationStatusBadge(myApplication.statut).label}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Description */}
                            {task.description && (
                              <p className="text-gray-600 mb-4 leading-relaxed">{task.description}</p>
                            )}

                            {/* Détails du poste */}
                            <div className="flex flex-wrap gap-4 text-sm">
                              {task.remuneration && (
                                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-xl">
                                  <span className="text-lg">💰</span>
                                  <div>
                                    <p className="text-xs text-gray-500">Rémunération</p>
                                    <p className="font-bold text-green-700">{Number(task.remuneration).toLocaleString()} XOF</p>
                                  </div>
                                </div>
                              )}
                              {task.valeur_parts > 0 && (
                                <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-xl">
                                  <span className="text-lg">📈</span>
                                  <div>
                                    <p className="text-xs text-gray-500">Parts offertes</p>
                                    <p className="font-bold text-purple-700">{task.valeur_parts}%</p>
                                  </div>
                                </div>
                              )}
                              {task.competences_requises && (
                                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-xl flex-1 min-w-[200px]">
                                  <span className="text-lg">🎯</span>
                                  <div>
                                    <p className="text-xs text-gray-500">Compétences requises</p>
                                    <p className="font-medium text-blue-700">{task.competences_requises}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="lg:w-48 flex flex-col gap-2">
                            {!task.assigned_user_id && user && !hasAlreadyApplied && (
                              <Link
                                to={`/apply/${task.id}`}
                                className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow hover:shadow-lg hover:scale-[1.02] transition-all text-center"
                              >
                                🚀 Postuler
                              </Link>
                            )}
                            
                            {isAssignedToOther && (
                              <span className="w-full py-3 bg-gray-100 text-gray-500 rounded-xl font-medium text-center">
                                🔒 Poste pourvu
                              </span>
                            )}
                            
                            {!task.assigned_user_id && user && hasAlreadyApplied && (
                              <span className={`w-full py-3 rounded-xl font-medium text-center ${
                                myApplication.statut === 'accepte' ? 'bg-green-100 text-green-700' :
                                myApplication.statut === 'refuse' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {myApplication.statut === 'en_attente' && '⏳ En attente'}
                                {myApplication.statut === 'accepte' && '✅ Acceptée'}
                                {myApplication.statut === 'refuse' && '❌ Refusée'}
                              </span>
                            )}
                            
                            {!user && !task.assigned_user_id && (
                              <Link
                                to="/login"
                                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition text-center"
                              >
                                Connectez-vous
                              </Link>
                            )}
                            
                            {isAssignedToMe && (
                              <span className="w-full py-3 bg-green-100 text-green-700 rounded-xl font-semibold text-center">
                                🎉 Vous avez ce rôle !
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                  <span className="text-6xl mb-4 block">💼</span>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun poste ouvert</h3>
                  <p className="text-gray-500">Le porteur du projet n'a pas encore publié de postes.</p>
                </div>
              )}
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {team.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {team.map((member, idx) => (
                    <Link 
                      key={idx} 
                      to={`/profile/${member.id}`}
                      className="p-4 bg-gradient-to-r from-gray-50 to-indigo-50 rounded-xl flex items-center gap-4 hover:shadow-md transition-all group"
                    >
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center font-bold text-white text-lg group-hover:scale-105 transition-transform">
                        {member.nom?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 group-hover:text-indigo-600 truncate">{member.nom}</p>
                        <p className="text-sm text-gray-500 truncate">{getTaskTypeInfo(member.task_type).label}</p>
                      </div>
                      <span className="text-indigo-400 group-hover:translate-x-1 transition-transform">→</span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="text-6xl mb-4 block">👥</span>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Équipe en construction</h3>
                  <p className="text-gray-500 mb-4">Soyez le premier à rejoindre ce projet !</p>
                  {tasks.length > 0 && (
                    <button 
                      onClick={() => setActiveTab('tasks')}
                      className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition"
                    >
                      Voir les postes ouverts
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map(img => (
                    <div 
                      key={img.id}
                      onClick={() => setSelectedImage(img)}
                      className="aspect-square rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                    >
                      <img
                        src={`${UPLOADS_URL}${img.chemin}`}
                        alt="Projet"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="text-6xl mb-4 block">📸</span>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Pas encore d'images</h3>
                  <p className="text-gray-500">Le porteur n'a pas encore ajouté de photos.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-5xl w-full">
              <img
                src={`${UPLOADS_URL}${selectedImage.chemin}`}
                alt="Projet"
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-800 hover:bg-gray-100 shadow-lg text-xl"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bouton flottant sticky "Postuler" - quand PAS de tâche spécifique */}
      {openTasksCount > 0 && canApply && activeTab !== 'tasks' && !highlightedTaskId && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <button
            onClick={scrollToTasks}
            className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-full font-bold text-lg shadow-2xl shadow-green-500/30 hover:shadow-green-500/50 hover:scale-105 transition-all duration-300"
          >
            <span className="text-2xl animate-bounce">🚀</span>
            Postuler
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
              {openTasksCount}
            </span>
          </button>
        </div>
      )}

      {/* Bouton flottant pour tâche spécifique (depuis "Pour Toi") */}
      {highlightedTaskId && canApply && tasks.find(t => String(t.id) === String(highlightedTaskId) && !t.assigned_user_id) && activeTab !== 'tasks' && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <Link
            to={`/apply/${highlightedTaskId}`}
            className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-full font-bold text-lg shadow-2xl shadow-green-500/30 hover:shadow-green-500/50 hover:scale-105 transition-all duration-300"
          >
            <span className="text-2xl animate-bounce">🚀</span>
            Postuler à ce poste
          </Link>
        </div>
      )}

      {/* Bouton flottant pour non-connectés */}
      {openTasksCount > 0 && !user && activeTab !== 'tasks' && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <Link
            to="/login"
            className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-full font-bold text-lg shadow-2xl shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:scale-105 transition-all duration-300"
          >
            <span className="text-2xl">🔐</span>
            Connectez-vous pour postuler
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
